import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchAudienceByCountry,
  clearAudienceByCountry,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';
import Pagination from 'components/common/Pagination';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import CountryFlag from 'components/common/CountryFlag';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { countries as countriesFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import './AnalyticsCountriesList.css';

const AnalyticsCountriesList = ({
  date,
  audienceByCountry,
  audienceByCountryError,
  fetchAudienceByCountry,
  clearAudienceByCountry,
  me,
}) => {
  const { t } = useTranslation();
  const itemsPerPage = 7; // define how many items must be on page
  const [itemsToDisplay, setItemsToDisplay] = useState({
    start: 1,
    end: itemsPerPage,
  });

  useEffect(() => {
    if (!audienceByCountry) {
      fetchAudienceByCountry(date);
    }
    return () => {
      clearAudienceByCountry();
      setItemsToDisplay({ start: 1, end: itemsPerPage });
    };
  }, [date]);

  if (audienceByCountryError) {
    return null;
  }

  if (audienceByCountry && Object.keys(audienceByCountry).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Views by Country")} text={t("Looks like you have no data to display yet…")} />;
  }

  const handlePaginationPageClick = (date) => {
    setItemsToDisplay({
      start: date.selected * itemsPerPage + 1,
      end: date.selected * itemsPerPage + itemsPerPage,
    });
  };

  const renderTableData = (audienceByCountryData) => {
    if (!audienceByCountryData) {
      return null;
    }
    const countPages = Math.ceil(Object.keys(audienceByCountryData).length / itemsPerPage);
    return (
      <>
        <table>
          <thead>
            <tr>
              <th />
              <th />
              <th>{t("Views")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(audienceByCountryData).slice(itemsToDisplay.start - 1, itemsToDisplay.end).map((i, index) => (
              <tr
                key={`analytics_countries_list_table_row_key_${index}`}
              >
                <td>{index + 1}.</td>
                <td>
                  <CountryFlag name={i.toLowerCase()} />&nbsp;&nbsp;
                  <span>{i}</span>
                </td>
                <td className="views">
                  {audienceByCountryData[i].views &&
                    <span className="value">{audienceByCountryData[i].views}&nbsp;&nbsp;</span>
                  }
                  {audienceByCountryData[i].percent &&
                    <span className="value-percent">({audienceByCountryData[i].percent}&#37;)</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {Object.keys(audienceByCountryData).length > itemsPerPage &&
          <Pagination
            containerClassName={'pagination-wrapper'}
            activeClassName={'active'}
            pageCount={countPages}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePaginationPageClick}
          />
        }
      </>
    );
  };

  return (
    <AnalyticsSection
      className="analytics-countries-list__component"
      header={
        <div className="title">
          <h3>{t("Views by Country")}</h3>
        </div>
      }
    >
      <div className="analytics-countries-list-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderTableData(countriesFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderTableData(audienceByCountry)}
          </>
        }
        <Loader active={!audienceByCountry} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsCountriesList.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchAudienceByCountry: PropTypes.func.isRequired,
  clearAudienceByCountry: PropTypes.func.isRequired,
  audienceByCountry: PropTypes.oneOfType([PropTypes.object]),
  audienceByCountryError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsCountriesList.defaultProps = {
  audienceByCountry: null,
  audienceByCountryError: null,
};

const mapStateToProps = state => ({
  audienceByCountry: state.analyticsReducer.audienceByCountry,
  audienceByCountryError: state.analyticsReducer.audienceByCountryError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchAudienceByCountry,
    clearAudienceByCountry,
  },
)(AnalyticsCountriesList);
